// Map-type resolution for the Amap renderer, kept pure so the roadmap/satellite/hybrid matrix is
// unit-testable without faking the JSAPI.
//
// Google's wrapper delegates this to the Maps UI: with mapType "user select" it shows Google's own
// map-type control and mirrors the user's choice back via onMapTypeIdChange. Amap's JSAPI has no
// equivalent built-in control, so the Kuika map-settings drawer owns the switch and the wrapper
// applies the layers itself.

/** The map types a Kuika Maps component can be configured with, minus the "user select" sentinel. */
export type AmapMapType = "roadmap" | "terrain" | "satellite" | "hybrid";

/** Which Amap tile layers must be attached for a given map type. */
export interface IAmapLayerVisibility {
  satellite: boolean;
  roadNet: boolean;
}

/**
 * The map type a freshly mounted map opens with. "user select" means "let the end user choose", so
 * it starts on the base map, exactly like the Google wrapper's constructor.
 */
export function resolveInitialMapType(configuredMapType: string | undefined): AmapMapType {
  if (!configuredMapType || configuredMapType === "user select") return "roadmap";
  return configuredMapType as AmapMapType;
}

/**
 * Layers for a map type. Amap has no terrain tile layer, so "terrain" renders the base map — a
 * documented gap of the Amap provider, not a silent bug.
 */
export function resolveLayerVisibility(mapType: AmapMapType): IAmapLayerVisibility {
  return {
    satellite: mapType === "satellite" || mapType === "hybrid",
    roadNet: mapType === "hybrid"
  };
}

/** The map type the drawer's satellite switch maps to. */
export function toggleSatelliteMapType(isSatelliteOn: boolean): AmapMapType {
  return isSatelliteOn ? "satellite" : "roadmap";
}

/** Whether the drawer's satellite switch should read as on for a map type. */
export function isSatelliteMapType(mapType: AmapMapType): boolean {
  return resolveLayerVisibility(mapType).satellite;
}
